import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
}

export interface ChatResponse {
  reply: string;
  suggestions: string[];
  role: 'assistant';
  provider?: string;
  token_usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
}

@Injectable({ providedIn: 'root' })
export class ChatbotService {

  private _isOpen = new BehaviorSubject<boolean>(false);
  isOpen$ = this._isOpen.asObservable();

  private _isLoading = new BehaviorSubject<boolean>(false);
  isLoading$ = this._isLoading.asObservable();

  /** In-memory conversation history (OpenAI message format) */
  private conversationHistory: ChatMessage[] = [];

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
  ) {}

  // ─── Panel visibility ──────────────────────────────────────────────────────

  open()  { this._isOpen.next(true);  }
  close() { this._isOpen.next(false); }
  toggle() { this._isOpen.next(!this._isOpen.value); }
  get isOpen(): boolean { return this._isOpen.value; }

  // ─── Conversation management ───────────────────────────────────────────────

  getHistory(): ChatMessage[] { return this.conversationHistory; }

  clearHistory() { this.conversationHistory = []; }

  /** Add a user or assistant message to local history */
  addMessage(role: 'user' | 'assistant', content: string) {
    this.conversationHistory.push({ role, content, timestamp: new Date() });
  }

  // ─── Send a message to TINA ───────────────────────────────────────────────

  sendMessage(userMessage: string): Observable<ChatResponse> {
    this.addMessage('user', userMessage);
    this._isLoading.next(true);

    const context = this.buildContext();

    // Send only role+content to backend (no timestamps)
    const messages = this.conversationHistory.map(m => ({
      role:    m.role,
      content: m.content,
    }));

    return this.http.post<ChatResponse>(environment.agentChat, { messages, context });
  }

  /** Call this after getting a response to add it to history */
  addAssistantResponse(reply: string) {
    this.addMessage('assistant', reply);
    this._isLoading.next(false);
  }

  setLoading(loading: boolean) { this._isLoading.next(loading); }

  // ─── Build context from local storage ─────────────────────────────────────

  private buildContext(): Record<string, any> {
    return {
      hospital_id:   this.localStorage.getHospitalId(),
      center_id:     this.localStorage.getCenterId(),
      user_id:       this.localStorage.getUserId(),
      hospital_name: this.localStorage.getHospitalName(),
      user_name:     this.localStorage.getProfileName(),
      token:         localStorage.getItem('token') || '',
    };
  }
}
